import React, { useState } from 'react';
import { MilestoneService } from '../services/milestoneService';
import type { CreateMilestoneData, BenefitData, SpendCondition } from '../types/milestone';

interface CreateMilestoneFormProps {
  cardId: string;
  cardName: string;
  bankName: string;
  bankSlug: string;
  cardSlug: string;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateMilestoneForm: React.FC<CreateMilestoneFormProps> = ({
  cardId,
  cardName,
  bankName,
  bankSlug,
  cardSlug,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateMilestoneData>({
    milestoneType: 'welcome',
    spendThreshold: 0,
    lastDateToAvail: null,
    description: '',
    rps: 0,
    cardId: cardId,
    spendConditions: [],
    benefits: [],
    validFrom: null,
    validUntil: null
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Benefit form state
  const [newBenefit, setNewBenefit] = useState<BenefitData>({
    benefitName: '',
    details: '',
    value: 0,
    category: '',
    isActive: true
  });

  // Spend condition form state
  const [newSpendCondition, setNewSpendCondition] = useState<SpendCondition>({
    min: 0,
    max: 1,
    description: ''
  });

  const handleInputChange = (field: keyof CreateMilestoneData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBenefitChange = (field: keyof BenefitData, value: any) => {
    setNewBenefit(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSpendConditionChange = (field: keyof SpendCondition, value: any) => {
    setNewSpendCondition(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addBenefit = () => {
    if (newBenefit.benefitName && newBenefit.details && newBenefit.value > 0) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, { ...newBenefit }]
      }));
      setNewBenefit({
        benefitName: '',
        details: '',
        value: 0,
        category: '',
        isActive: true
      });
    }
  };

  const removeBenefit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const addSpendCondition = () => {
    setFormData(prev => ({
      ...prev,
      spendConditions: [...(prev.spendConditions || []), { ...newSpendCondition }]
    }));
    setNewSpendCondition({
      min: 0,
      max: 1,
      description: ''
    });
  };

  const removeSpendCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      spendConditions: prev.spendConditions?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description || formData.spendThreshold <= 0) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await MilestoneService.createMilestone(formData);
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating milestone:', err);
      setError(err instanceof Error ? err.message : 'Failed to create milestone');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create New Milestone</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
            Milestone created successfully!
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Milestone Type *</label>
                <select
                  value={formData.milestoneType}
                  onChange={(e) => handleInputChange('milestoneType', e.target.value as any)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="welcome">Welcome</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                  <option value="half_yearly">Half Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spend Threshold (₹) *</label>
                <input
                  type="number"
                  value={formData.spendThreshold}
                  onChange={(e) => handleInputChange('spendThreshold', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reward Points (RPs) *</label>
                <input
                  type="number"
                  value={formData.rps}
                  onChange={(e) => handleInputChange('rps', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Date to Avail</label>
                <select
                  value={formData.lastDateToAvail || ''}
                  onChange={(e) => handleInputChange('lastDateToAvail', e.target.value === '' ? null : e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="null">No limit</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half_yearly">Half Yearly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                required
                placeholder="Describe the milestone conditions and benefits"
              />
            </div>
          </div>

          {/* Spend Conditions */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Spend Conditions (Optional)</h4>
            
            {formData.spendConditions && formData.spendConditions.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.spendConditions.map((condition, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                    <div>
                      <span className="text-sm font-medium">Min: {condition.min} • Max: {condition.max}</span>
                      {condition.description && (
                        <p className="text-xs text-gray-500">{condition.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSpendCondition(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Transactions</label>
                <input
                  type="number"
                  value={newSpendCondition.min}
                  onChange={(e) => handleSpendConditionChange('min', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Transactions</label>
                <input
                  type="number"
                  value={newSpendCondition.max}
                  onChange={(e) => handleSpendConditionChange('max', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newSpendCondition.description}
                  onChange={(e) => handleSpendConditionChange('description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., One time per quarter"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={addSpendCondition}
              className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Condition
            </button>
          </div>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Benefits (Optional)</h4>
            
            {formData.benefits.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white rounded border">
                    <div>
                      <span className="text-sm font-medium">{benefit.benefitName}</span>
                      <p className="text-xs text-gray-500">{benefit.details}</p>
                      <span className="text-xs font-medium text-green-600">
                        {formatCurrency(benefit.value)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBenefit(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benefit Name</label>
                <input
                  type="text"
                  value={newBenefit.benefitName}
                  onChange={(e) => handleBenefitChange('benefitName', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Netflix Premium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value (₹)</label>
                <input
                  type="number"
                  value={newBenefit.value}
                  onChange={(e) => handleBenefitChange('value', Number(e.target.value))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                <input
                  type="text"
                  value={newBenefit.details}
                  onChange={(e) => handleBenefitChange('details', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 6 months subscription"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={newBenefit.category}
                  onChange={(e) => handleBenefitChange('category', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., entertainment"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={addBenefit}
              disabled={!newBenefit.benefitName || !newBenefit.details || newBenefit.value <= 0}
              className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Benefit
            </button>
          </div>

          {/* Card Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-gray-900 mb-4">Card Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
                <input
                  type="text"
                  value={bankName}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card</label>
                <input
                  type="text"
                  value={cardName}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Milestone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMilestoneForm;
